import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Card, Badge, Modal } from '../common';
import { useToastContext } from '../common/Toast/ToastProvider';
import reportsV2Service from '../../services/reportsV2Service';
import './ReportEditPage.css';

const TAB_CONFIG = [
  { id: 'basic', label: '기본 정보' },
  { id: 'resource', label: '리소스 현황' },
  { id: 'metric', label: '메트릭' },
  { id: 'inspection', label: '검사 결과' },
  { id: 'ai', label: 'AI 분석' },
  { id: 'history', label: '수정 이력' },
];

const RESOURCE_LABELS = {
  ec2:           { label: 'EC2',           fields: { running: '실행중', stopped: '중지됨', publicIp: '퍼블릭 IP' } },
  s3:            { label: 'S3',            fields: { total: '총 버킷', public: '퍼블릭', encrypted: '암호화' } },
  rds:           { label: 'RDS',           fields: { running: '실행중', encrypted: '암호화', public: '퍼블릭' } },
  iam:           { label: 'IAM',           fields: { users: '사용자', accessKeys: '액세스키', rootKeys: '루트키' } },
  vpc:           { label: 'VPC',           fields: { total: '총 VPC', default: '기본', subnets: '서브넷' } },
  elb:           { label: 'ELB',           fields: { total: '총 LB', application: 'ALB', network: 'NLB' } },
  cloudfront:    { label: 'CloudFront',    fields: { distributions: '배포', enabled: '활성', origins: '오리진' } },
  ecs:           { label: 'ECS',           fields: { clusters: '클러스터', services: '서비스', tasks: '태스크' } },
  gateway:       { label: 'API Gateway',   fields: { apis: 'API', protocols: '프로토콜', endpoints: '엔드포인트' } },
  route53:       { label: 'Route53',       fields: { hostedZones: '호스팅 영역', records: '레코드', publicZones: '퍼블릭 영역' } },
  routetable:    { label: 'RouteTable',    fields: { total: '총 테이블', main: '메인', custom: '커스텀' } },
  securitygroup: { label: 'SecurityGroup', fields: { total: '총 그룹', default: '기본', custom: '커스텀' } },
  waf:           { label: 'WAF',           fields: { webAcls: 'Web ACL', enabled: '활성', rules: '규칙' } },
};

const ReportEditPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const toast = useToastContext();
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [report, setReport] = useState(null);
  const [results, setResults] = useState({});

  const [metadata, setMetadata] = useState({});
  const [resourceData, setResourceData] = useState({});
  const [metricData, setMetricData] = useState({});
  const [inspectionData, setInspectionData] = useState({});
  const [aiAnalysis, setAiAnalysis] = useState('');
  const [deletedInspectionItems, setDeletedInspectionItems] = useState([]);
  const [regenerating, setRegenerating] = useState(false);

  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', onConfirm: null });

  const showConfirm = (title, message) => new Promise(resolve => {
    setConfirmModal({ open: true, title, message, onConfirm: () => { setConfirmModal(p => ({ ...p, open: false })); resolve(true); } });
  });
  const closeConfirm = () => setConfirmModal(p => ({ ...p, open: false }));

  useEffect(() => { loadReport(); }, [reportId]);

  useEffect(() => {
    if (!report) return;
    const blocked = report.status === 'PENDING' || report.status === 'IN_PROGRESS' || report.aiRegenerating;
    if (!blocked) return;
    const interval = setInterval(async () => {
      try {
        const res = await reportsV2Service.getReport(reportId);
        const r = res.data;
        setReport(r);
        if (!(r.status === 'PENDING' || r.status === 'IN_PROGRESS' || r.aiRegenerating)) {
          clearInterval(interval);
          const resultsRes = await reportsV2Service.getReportResults(reportId);
          const res2 = resultsRes.data;
          setResults(res2);
          if (res2.aiAnalysis?.analysis) setAiAnalysis(res2.aiAnalysis.analysis);
          toast.showToast({ type: 'success', title: '완료', message: '처리가 완료되었습니다.' });
        }
      } catch { clearInterval(interval); }
    }, 5000);
    return () => clearInterval(interval);
  }, [report?.status, report?.aiRegenerating]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const [reportRes, resultsRes] = await Promise.all([
        reportsV2Service.getReport(reportId),
        reportsV2Service.getReportResults(reportId)
      ]);
      const r = reportRes.data;
      const res = resultsRes.data;
      setReport(r);
      setResults(res);
      setMetadata({ ...r.metadata });

      const rData = {};
      Object.keys(res).filter(k => k.startsWith('resource_')).forEach(k => {
        const service = k.replace('resource_', '');
        if (!res[k].error) rData[service] = { ...res[k] };
      });
      setResourceData(rData);

      const mData = {};
      Object.keys(res).filter(k => k.startsWith('metric_')).forEach(k => {
        const key = k.replace('metric_', '');
        if (!res[k].error) mData[key] = res[k];
      });
      setMetricData(mData);

      if (res.resourceInspection?.data) {
        const iData = {};
        Object.entries(res.resourceInspection.data).forEach(([itemId, itemData]) => {
          iData[itemId] = { ...itemData, allFindings: (itemData.allFindings || []).map(f => ({ ...f })) };
        });
        setInspectionData(iData);
      }
      if (res.aiAnalysis?.analysis) setAiAnalysis(res.aiAnalysis.analysis);
    } catch {
      toast.showToast({ type: 'error', title: '오류', message: '보고서를 불러오는데 실패했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMetadata = async () => {
    setSaving(true);
    try {
      const prev = report.metadata;
      const changes = [];
      if (prev.authorName !== metadata.authorName) changes.push(`작성자: "${prev.authorName}" → "${metadata.authorName}"`);
      if (prev.reportStartDate !== metadata.reportStartDate) changes.push(`시작일: ${prev.reportStartDate} → ${metadata.reportStartDate}`);
      if (prev.reportEndDate !== metadata.reportEndDate) changes.push(`종료일: ${prev.reportEndDate} → ${metadata.reportEndDate}`);
      await reportsV2Service.updateMetadata(reportId, metadata);
      if (changes.length > 0) {
        await reportsV2Service.appendEditHistory(reportId, {
          tab: '기본 정보', action: '수정', summary: changes.join(' / '),
          snapshot: { type: 'metadata', data: { ...prev } }
        });
      }
      toast.showToast({ type: 'success', title: '저장 완료', message: '기본 정보가 저장되었습니다.' });
      await loadReport();
    } catch (error) {
      toast.showToast({ type: 'error', title: '저장 실패', message: error.message });
    } finally { setSaving(false); }
  };

  const handleSaveResource = async (service) => {
    setSaving(true);
    try {
      const prev = results[`resource_${service}`] || {};
      const curr = resourceData[service];
      const config = RESOURCE_LABELS[service];
      const changes = Object.entries(config.fields)
        .filter(([field]) => prev[field] !== curr[field])
        .map(([field, label]) => `${label}: ${prev[field] ?? 0} → ${curr[field]}`);
      await reportsV2Service.updateResult(reportId, `resource_${service}`, curr);
      if (changes.length > 0) {
        await reportsV2Service.appendEditHistory(reportId, {
          tab: '리소스 현황', action: '수정', summary: `${config.label} - ${changes.join(', ')}`,
          snapshot: { type: 'result', resultType: `resource_${service}`, data: { ...prev } }
        });
      }
      toast.showToast({ type: 'success', title: '저장 완료', message: `${config.label} 저장되었습니다.` });
      await loadReport();
    } catch (error) {
      toast.showToast({ type: 'error', title: '저장 실패', message: error.message });
    } finally { setSaving(false); }
  };

  const handleSaveInspection = async () => {
    setSaving(true);
    try {
      const updated = { ...results.resourceInspection, data: inspectionData };
      await reportsV2Service.updateResult(reportId, 'resourceInspection', updated);
      const prevInspection = results.resourceInspection || {};
      const historyEntries = [];
      if (deletedInspectionItems.length > 0) {
        historyEntries.push({
          tab: '검사 결과', action: '삭제', summary: `검사 항목 삭제: ${deletedInspectionItems.join(', ')}`,
          snapshot: { type: 'result', resultType: 'resourceInspection', data: prevInspection }
        });
        setDeletedInspectionItems([]);
      }
      const prevData = prevInspection.data || {};
      Object.entries(inspectionData).forEach(([itemId, itemData]) => {
        const prevItem = prevData[itemId];
        if (!prevItem) return;
        const prevCount = prevItem.allFindings?.length || 0;
        const currCount = itemData.allFindings?.length || 0;
        if (prevCount !== currCount) {
          historyEntries.push({
            tab: '검사 결과', action: '수정', summary: `${itemId} - finding: ${prevCount}건 → ${currCount}건`,
            snapshot: { type: 'result', resultType: 'resourceInspection', data: prevInspection }
          });
        }
      });
      for (const entry of historyEntries) await reportsV2Service.appendEditHistory(reportId, entry);
      toast.showToast({ type: 'success', title: '저장 완료', message: '검사 결과가 저장되었습니다.' });
      await loadReport();
    } catch (error) {
      toast.showToast({ type: 'error', title: '저장 실패', message: error.message });
    } finally { setSaving(false); }
  };

  const handleSaveAI = async () => {
    setSaving(true);
    try {
      const prevAI = results.aiAnalysis || {};
      const prevLen = prevAI.analysis?.length || 0;
      await reportsV2Service.updateAIAnalysis(reportId, aiAnalysis);
      await reportsV2Service.appendEditHistory(reportId, {
        tab: 'AI 분석', action: '수정', summary: `내용 수정 (${prevLen}자 → ${aiAnalysis.length}자)`,
        snapshot: { type: 'aiAnalysis', data: { ...prevAI } }
      });
      toast.showToast({ type: 'success', title: '저장 완료', message: 'AI 분석이 저장되었습니다.' });
      await loadReport();
    } catch (error) {
      toast.showToast({ type: 'error', title: '저장 실패', message: error.message });
    } finally { setSaving(false); }
  };

  const removeResource = async (service) => {
    if (!await showConfirm('리소스 삭제', `${RESOURCE_LABELS[service]?.label || service} 리소스를 삭제하시겠습니까?`)) return;
    setSaving(true);
    try {
      const prev = results[`resource_${service}`] || {};
      await reportsV2Service.appendEditHistory(reportId, {
        tab: '리소스 현황', action: '삭제',
        summary: `${RESOURCE_LABELS[service]?.label} 리소스 삭제`,
        snapshot: { type: 'resultDelete', resultType: `resource_${service}`, data: prev }
      });
      await reportsV2Service.deleteResult(reportId, `resource_${service}`, { skipHistory: true });
      toast.showToast({ type: 'success', title: '삭제 완료', message: `${RESOURCE_LABELS[service]?.label} 삭제되었습니다.` });
      await loadReport();
    } catch (error) {
      toast.showToast({ type: 'error', title: '삭제 실패', message: error.message });
    } finally { setSaving(false); }
  };

  const removeInspectionItem = async (itemId) => {
    if (!await showConfirm('항목 삭제', `'${itemId}' 항목을 삭제하시겠습니까?`)) return;
    setDeletedInspectionItems(prev => [...prev, itemId]);
    setInspectionData(prev => { const n = { ...prev }; delete n[itemId]; return n; });
  };

  const removeFinding = (itemId, idx) => {
    setInspectionData(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], allFindings: prev[itemId].allFindings.filter((_, i) => i !== idx) }
    }));
  };

  const handleRegenerateAI = async () => {
    if (!await showConfirm('AI 재분석', 'AI 분석을 다시 요청하시겠습니까?\n기존 내용은 새로운 결과로 대체됩니다.')) return;
    setRegenerating(true);
    try {
      await reportsV2Service.regenerateAI(reportId);
      toast.showToast({ type: 'info', title: 'AI 분석 시작', message: '완료 후 자동으로 반영됩니다.' });
      await loadReport();
    } catch (error) {
      toast.showToast({ type: 'error', title: '실패', message: error.message });
    } finally { setRegenerating(false); }
  };

  const clearAIAnalysis = async () => {
    if (!await showConfirm('AI 분석 삭제', 'AI 분석 내용을 삭제하시겠습니까?')) return;
    setSaving(true);
    try {
      const prevAI = results.aiAnalysis || {};
      await reportsV2Service.appendEditHistory(reportId, {
        tab: 'AI 분석', action: '삭제',
        summary: 'AI 분석 삭제',
        snapshot: { type: 'resultDelete', resultType: 'aiAnalysis', data: prevAI }
      });
      await reportsV2Service.deleteResult(reportId, 'aiAnalysis', { skipHistory: true });
      toast.showToast({ type: 'success', title: '삭제 완료', message: 'AI 분석이 삭제되었습니다.' });
      await loadReport();
    } catch (error) {
      toast.showToast({ type: 'error', title: '삭제 실패', message: error.message });
    } finally { setSaving(false); }
  };

  const handleRollback = async (historyIndex) => {
    if (!await showConfirm('변경 취소', '이 수정을 취소하고 이전 상태로 되돌리시겠습니까?')) return;
    setSaving(true);
    try {
      await reportsV2Service.rollbackHistory(reportId, historyIndex);
      await loadReport();
      toast.showToast({ type: 'success', title: '롤백 완료', message: '변경이 취소되었습니다.' });
    } catch (error) {
      toast.showToast({ type: 'error', title: '롤백 실패', message: error.message });
    } finally { setSaving(false); }
  };

  /* ── 로딩 / 에러 ── */
  if (loading) {
    return (
      <div className="re-fullstate">
        <div className="re-spinner" />
        <p>보고서를 불러오는 중...</p>
      </div>
    );
  }
  if (!report) {
    return (
      <div className="re-fullstate">
        <p className="re-fullstate__title">보고서를 찾을 수 없습니다</p>
        <p className="re-fullstate__desc">삭제되었거나 잘못된 경로입니다.</p>
        <Button variant="secondary" onClick={() => navigate('/report')}>목록으로</Button>
      </div>
    );
  }

  const isBlocked = report.status === 'PENDING' || report.status === 'IN_PROGRESS' || !!report.aiRegenerating;
  const hasResource = Object.keys(resourceData).length > 0;
  const hasMetric = Object.keys(metricData).length > 0;
  const hasInspection = Object.keys(inspectionData).length > 0;
  const hasAI = !!results.aiAnalysis;
  const editHistory = report.editHistory || [];

  const visibleTabs = TAB_CONFIG.filter(t => {
    if (t.id === 'resource') return hasResource;
    if (t.id === 'metric') return hasMetric;
    if (t.id === 'inspection') return hasInspection;
    if (t.id === 'ai') return hasAI;
    return true;
  });

  const removeMetric = async (metricKey) => {
    if (!await showConfirm('메트릭 삭제', `'${metricData[metricKey]?.config?.name || metricKey}' 메트릭을 삭제하시겠습니까?\n메트릭 삭제는 롤백할 수 없습니다.`)) return;
    setSaving(true);
    try {
      await reportsV2Service.deleteResult(reportId, `metric_${metricKey}`);
      toast.showToast({ type: 'success', title: '삭제 완료', message: '메트릭이 삭제되었습니다.' });
      await loadReport();
    } catch (error) {
      toast.showToast({ type: 'error', title: '삭제 실패', message: error.message });
    } finally { setSaving(false); }
  };

  const removeMetricResource = async (metricKey, resourceIndex) => {
    const metric = metricData[metricKey];
    const prev = metric.data?.resources || [];
    const removed = prev[resourceIndex];
    if (!await showConfirm('리소스 삭제', `'${removed?.resourceId || ''}' 리소스를 삭제하시겠습니까?\n메트릭 수정은 롤백할 수 없습니다.`)) return;
    const updated = prev.filter((_, i) => i !== resourceIndex);
    const newData = { ...metric, data: { ...metric.data, resources: updated } };
    setSaving(true);
    try {
      await reportsV2Service.updateResult(reportId, `metric_${metricKey}`, newData);
      await reportsV2Service.appendEditHistory(reportId, {
        tab: '메트릭', action: '수정',
        summary: `${metric.config?.name || metricKey} - 리소스 ${removed?.resourceId || ''} 삭제 (${prev.length}건 → ${updated.length}건)`
      });
      toast.showToast({ type: 'success', title: '저장 완료', message: '리소스가 삭제되었습니다.' });
      await loadReport();
    } catch (error) {
      toast.showToast({ type: 'error', title: '저장 실패', message: error.message });
    } finally { setSaving(false); }
  };

  return (
    <div className="re-page">
      {/* ── 상단 바 ── */}
      <header className="re-topbar">
        <div className="re-topbar__left">
          <button className="re-topbar__back" onClick={() => navigate('/report')}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            목록
          </button>
          <div className="re-topbar__divider" />
          <div className="re-topbar__title">
            <h1>보고서 편집</h1>
            <span className="re-topbar__sub">
              {report.metadata?.authorName} &middot; {report.metadata?.reportStartDate} ~ {report.metadata?.reportEndDate}
            </span>
          </div>
        </div>
        {isBlocked && (
          <div className="re-topbar__status">
            <span className="re-topbar__status-dot" />
            {report.status === 'PENDING' || report.status === 'IN_PROGRESS' ? '보고서 생성 중...' : 'AI 분석 진행 중...'}
          </div>
        )}
      </header>

      {/* ── 본문: 사이드 + 컨텐츠 ── */}
      <div className="re-body">
        <nav className="re-sidebar">
          {visibleTabs.map(tab => (
            <button
              key={tab.id}
              className={`re-sidebar__item ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        <main className="re-main">
          {/* 기본 정보 */}
          {activeTab === 'basic' && (
            <section className="re-section">
              <div className="re-section__head">
                <h2>기본 정보</h2>
              </div>
              <div className="re-form-grid">
                <div className="re-field re-field--span2">
                  <label className="re-label">작성자</label>
                  <input className="re-input" type="text" value={metadata.authorName || ''} onChange={e => setMetadata(p => ({ ...p, authorName: e.target.value }))} disabled={isBlocked} />
                </div>
                <div className="re-field">
                  <label className="re-label">시작일</label>
                  <input className="re-input" type="date" value={metadata.reportStartDate || ''} onChange={e => setMetadata(p => ({ ...p, reportStartDate: e.target.value }))} disabled={isBlocked} />
                </div>
                <div className="re-field">
                  <label className="re-label">종료일</label>
                  <input className="re-input" type="date" value={metadata.reportEndDate || ''} onChange={e => setMetadata(p => ({ ...p, reportEndDate: e.target.value }))} disabled={isBlocked} />
                </div>
              </div>
              <div className="re-section__foot">
                <Button variant="primary" loading={saving} onClick={handleSaveMetadata} disabled={saving || isBlocked}>변경사항 저장</Button>
              </div>
            </section>
          )}

          {/* 리소스 현황 */}
          {activeTab === 'resource' && (
            <section className="re-section">
              <div className="re-section__head">
                <h2>리소스 현황</h2>
                <Badge variant="neutral" size="sm">{Object.keys(resourceData).length}개</Badge>
              </div>
              <div className="re-resource-grid">
                {Object.entries(resourceData).map(([service, data]) => {
                  const config = RESOURCE_LABELS[service];
                  if (!config) return null;
                  return (
                    <div key={service} className="re-resource-card">
                      <div className="re-resource-card__head">
                        <span className="re-resource-card__name">{config.label}</span>
                        <button className="re-btn-text re-btn-text--danger" onClick={() => removeResource(service)} disabled={saving || isBlocked}>삭제</button>
                      </div>
                      <div className="re-resource-card__fields">
                        {Object.entries(config.fields).map(([field, label]) => (
                          <div key={field} className="re-field-inline">
                            <label className="re-label">{label}</label>
                            <input
                              className="re-input re-input--sm"
                              type="number"
                              min="0"
                              value={data[field] ?? 0}
                              disabled={isBlocked}
                              onChange={e => setResourceData(prev => ({
                                ...prev,
                                [service]: { ...prev[service], [field]: parseInt(e.target.value) || 0 }
                              }))}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="re-resource-card__foot">
                        <Button variant="primary" size="sm" loading={saving} onClick={() => handleSaveResource(service)} disabled={saving || isBlocked}>저장</Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* 메트릭 */}
          {activeTab === 'metric' && (
            <section className="re-section">
              <div className="re-section__head">
                <h2>메트릭</h2>
                <Badge variant="neutral" size="sm">{Object.keys(metricData).length}개</Badge>
              </div>
              <div className="re-metric-list">
                {Object.entries(metricData).map(([metricKey, metric]) => {
                  const config = metric.config || {};
                  const resources = metric.data?.resources || [];
                  return (
                    <div key={metricKey} className="re-metric-item">
                      <div className="re-metric-item__head">
                        <div className="re-metric-item__info">
                          <span className="re-metric-item__name">{config.name || metricKey}</span>
                          <Badge variant="info" size="sm">{config.service}</Badge>
                          <Badge variant="neutral" size="sm">{config.type} / {config.metric}</Badge>
                        </div>
                        <div className="re-metric-item__actions">
                          <Badge variant="neutral" size="sm">{resources.length}개 리소스</Badge>
                          <button className="re-btn-text re-btn-text--danger" onClick={() => removeMetric(metricKey)} disabled={saving || isBlocked}>삭제</button>
                        </div>
                      </div>
                      {resources.length > 0 && (
                        <div className="re-metric-table-wrap">
                          <table className="re-metric-table">
                            <thead>
                              <tr>
                                <th>리소스 ID</th>
                                <th>이름</th>
                                <th>타입</th>
                                <th>리전</th>
                                <th>메트릭</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {resources.map((r, ri) => (
                                <tr key={ri}>
                                  <td className="re-metric-table__id">{r.resourceId || '-'}</td>
                                  <td>{r.resourceName || '-'}</td>
                                  <td>{r.resourceType || '-'}</td>
                                  <td>{r.region || '-'}</td>
                                  <td className="re-metric-table__metrics">
                                    {r.metrics ? Object.entries(r.metrics).map(([k, v]) => (
                                      <span key={k} className="re-metric-tag">{k}: {typeof v === 'number' ? v.toFixed(2) : JSON.stringify(v)}</span>
                                    )) : '-'}
                                  </td>
                                  <td>
                                    <button className="re-btn-icon" onClick={() => removeMetricResource(metricKey, ri)} disabled={isBlocked} title="리소스 삭제">
                                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11 3L3 11M3 3l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* 검사 결과 */}
          {activeTab === 'inspection' && (
            <section className="re-section">
              <div className="re-section__head">
                <h2>검사 결과</h2>
                <div className="re-section__head-actions">
                  <Badge variant="neutral" size="sm">{Object.keys(inspectionData).length}개</Badge>
                  <Button variant="primary" size="sm" loading={saving} onClick={handleSaveInspection} disabled={saving || isBlocked}>전체 저장</Button>
                </div>
              </div>
              <div className="re-inspection-list">
                {Object.entries(inspectionData).map(([itemId, itemData]) => (
                  <div key={itemId} className="re-inspection-item">
                    <div className="re-inspection-item__head">
                      <div className="re-inspection-item__info">
                        <Badge variant="info" size="sm">{itemData.serviceType}</Badge>
                        <span className="re-inspection-item__id">{itemId}</span>
                      </div>
                      <div className="re-inspection-item__actions">
                        {itemData.allFindings?.length > 0
                          ? <Badge variant="warning" size="sm">{itemData.allFindings.length}건 발견</Badge>
                          : <Badge variant="success" size="sm">이상 없음</Badge>
                        }
                        <button className="re-btn-icon" onClick={() => removeInspectionItem(itemId)} disabled={isBlocked} title="삭제">
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11 3L3 11M3 3l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                        </button>
                      </div>
                    </div>
                    {itemData.allFindings?.length > 0 && (
                      <div className="re-findings">
                        {itemData.allFindings.map((finding, fi) => (
                          <div key={fi} className="re-finding-row">
                            <div className="re-finding-row__fields">
                              <div className="re-field">
                                <label className="re-label">리소스 ID</label>
                                <input className="re-input re-input--readonly" type="text" value={finding.resourceId || ''} readOnly />
                              </div>
                              <div className="re-field">
                                <label className="re-label">리소스 타입</label>
                                <input className="re-input re-input--readonly" type="text" value={finding.resourceType || ''} readOnly />
                              </div>
                              <div className="re-field re-field--span2">
                                <label className="re-label">이슈</label>
                                <textarea className="re-textarea re-input--readonly" value={finding.issue || ''} readOnly rows={2} />
                              </div>
                            </div>
                            <button className="re-btn-icon" onClick={() => removeFinding(itemId, fi)} disabled={isBlocked} title="삭제">
                              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11 3L3 11M3 3l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* AI 분석 */}
          {activeTab === 'ai' && (
            <section className="re-section">
              <div className="re-section__head">
                <h2>AI 분석</h2>
                <div className="re-section__head-actions">
                  <Button variant="secondary" size="sm" loading={regenerating} onClick={handleRegenerateAI} disabled={saving || regenerating || isBlocked}>재분석 요청</Button>
                  <button className="re-btn-text re-btn-text--danger" onClick={clearAIAnalysis} disabled={saving || isBlocked}>삭제</button>
                </div>
              </div>
              <textarea
                className="re-textarea re-textarea--ai"
                value={aiAnalysis}
                onChange={e => setAiAnalysis(e.target.value)}
                rows={28}
                placeholder="AI 분석 내용..."
                disabled={isBlocked}
              />
              <div className="re-section__foot">
                <span className="re-charcount">{aiAnalysis.length.toLocaleString()}자</span>
                <Button variant="primary" loading={saving} onClick={handleSaveAI} disabled={saving || isBlocked}>변경사항 저장</Button>
              </div>
            </section>
          )}

          {/* 수정 이력 */}
          {activeTab === 'history' && (
            <section className="re-section">
              <div className="re-section__head">
                <h2>수정 이력</h2>
                {editHistory.length > 0 && <Badge variant="neutral" size="sm">{editHistory.length}건</Badge>}
              </div>
              {editHistory.length === 0 ? (
                <div className="re-empty">
                  <p>아직 수정 이력이 없습니다.</p>
                </div>
              ) : (
                <div className="re-history">
                  <div className="re-history__header">
                    <span>구분</span>
                    <span>탭</span>
                    <span>내용</span>
                    <span>시간</span>
                    <span></span>
                  </div>
                  {[...editHistory].reverse().map((entry, i) => {
                    const realIndex = editHistory.length - 1 - i;
                    const canRollback = !!entry.snapshot && !entry.rolledBack && entry.action !== '롤백';
                    return (
                      <div key={i} className={`re-history__row ${entry.rolledBack ? 're-history__row--disabled' : ''}`}>
                        <span>
                          <Badge variant={entry.action === '삭제' ? 'error' : entry.action === '롤백' ? 'warning' : 'info'} size="sm">{entry.action}</Badge>
                        </span>
                        <span className="re-history__tab">{entry.tab}</span>
                        <span className="re-history__summary">{entry.rolledBack ? <s>{entry.summary}</s> : entry.summary}</span>
                        <span className="re-history__time">{new Date(entry.timestamp).toLocaleString('ko-KR')}</span>
                        <span className="re-history__actions">
                          {canRollback && (
                            <button className="re-btn-text re-btn-text--rollback" onClick={() => handleRollback(realIndex)} disabled={saving || isBlocked}>취소</button>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}
        </main>
      </div>

      {/* Confirm 모달 */}
      <Modal isOpen={confirmModal.open} onClose={closeConfirm} size="sm">
        <div className="re-confirm">
          <h3>{confirmModal.title}</h3>
          <p>{confirmModal.message}</p>
          <div className="re-confirm__actions">
            <Button variant="secondary" size="sm" onClick={closeConfirm}>취소</Button>
            <Button variant="primary" size="sm" onClick={confirmModal.onConfirm}>확인</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ReportEditPage;
